import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Purpose: Drives the `predictive-search-component` custom element - fetches and
 *          renders live/recently-viewed/empty-state predictive search results via
 *          section-render fetches, morphs them into the persistent
 *          `#predictive-search-results` listbox root, and implements the WAI-ARIA
 *          combobox keyboard pattern (Arrow nav, aria-activedescendant, Escape,
 *          Enter) over those results.
 *
 * Public API: customElements.define('predictive-search-component',
 *             PredictiveSearchComponent) - consumed declaratively via the
 *             `<predictive-search-component>` tag (see snippets/sd-predictive-search.liquid,
 *             snippets/search-modal.liquid). No exported symbols; behavior is
 *             reached only through DOM refs (`on:input`, `on:keydown`, `on:click`
 *             bindings in the host markup) and required refs (searchInput,
 *             predictiveSearchResults, resetButton).
 *
 * Upstream deps: @theme/component (Component base + ref binding), @theme/utilities
 *                (debounce/onAnimationEnd/prefersReducedMotion), @theme/section-renderer
 *                (fetches rendered section HTML), @theme/morph (DOM diff/patch),
 *                @theme/recently-viewed-products (localStorage-backed product id
 *                list), @theme/dialog (optional host dialog open/close events).
 *                Server-rendered markup contract: every `role="option"` node
 *                rendered by predictive-search-products-list.liquid /
 *                predictive-search-resource-list.liquid / predictive-search.liquid
 *                MUST carry a globally-unique id within any single listbox render
 *                (see the `option_group` param on predictive-search-products-list.liquid)
 *                - #currentIndex's setter relies on that uniqueness for
 *                aria-activedescendant to resolve to the correct DOM node.
 *
 * Downstream consumers: sections/predictive-search.liquid (data-section-id target
 *                        for live query + recently-viewed-by-id fetches),
 *                        sections/predictive-search-empty.liquid (data-section-id
 *                        target for the empty/reset state), sd-inline-search
 *                        (snippets/sd-predictive-search.liquid, owns panel
 *                        open/close visibility only).
 *
 * Failure modes: network/fetch errors from sectionRenderer propagate as unhandled
 *                rejections (deliberately re-thrown in #getSearchResults's .catch
 *                unless aborted) rather than being swallowed. #resetSearch throws
 *                synchronously if the empty-section fetch returns no matching
 *                markup. The #morphResults defensive fallback (mismatched/missing
 *                `#predictive-search-results` id on either side) logs a
 *                console.warn and falls back to a direct morph that can corrupt
 *                the listbox root's own attributes (see the SWAG-147 note on
 *                #morphResults) - treat that warning as a signal something
 *                upstream changed the expected markup shape. All fetches are
 *                internally aborted/sequence-guarded (#requestSeq, AbortController)
 *                so a stale response is a safe no-op, not a re-throw.
 *
 * Performance: debounced (200ms search input, 100ms reset) to avoid a fetch per
 *              keystroke; only one in-flight fetch is kept live at a time
 *              (#createAbortController aborts the previous one).
 */

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * SWAG-52: monotonic generation stamp for search/reset requests. Every
   * request-initiating call captures the post-increment value; at each morph
   * point we bail if a newer request has since been initiated, so a slower,
   * stale response (e.g. the idle empty-state preload racing a live search)
   * can never paint over fresher results. Complements (does not replace) the
   * existing AbortController cooperative-bail checks.
   * @type {number}
   */
  #requestSeq = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // SWAG-148: the server-rendered initial markup (recently-viewed/default
    // products) isn't run through #morphResults, so normalize its tab order
    // on first connect too - see #syncResultItemsTabIndex.
    this.#syncResultItemsTabIndex();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, {
        signal,
        once: true,
      });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    // SWAG-52: the on-load idle preload (requestIdleCallback -> #loadEmptyState)
    // used to race a user's already-typed query and clobber it with the static
    // empty-state collection. Bail without marking loaded (and without
    // clearing the input or morphing) so this can still engage later once the
    // input is empty again - e.g. via #handleDialogOpen.
    if (this.refs.searchInput.value.trim().length > 0) return;
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    // requireEmpty: re-validated again when the debounce actually fires -
    // see the resetSearch jsdoc.
    this.resetSearch(false, true);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(
          container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * SWAG-148: makes every result option's interior link non-tabbable so real
   * Tab focus skips the listbox entirely, matching the WAI-ARIA
   * combobox-with-listbox pattern (options are reached via Arrow keys +
   * aria-activedescendant, not the browser's native Tab order). The
   * suggestion pills already render with `tabindex="-1"` in markup; this
   * covers the category/page/article rows and product cards (the latter's
   * link lives inside the shared `resource-card` snippet, which must stay
   * normally tabbable for its other call sites, so it's normalized here
   * instead of in the shared markup).
   */
  #syncResultItemsTabIndex() {
    this.#allResultsItems?.forEach((item) => {
      const link = item.querySelector('a');
      if (link && link.getAttribute('tabindex') !== '-1') {
        link.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * SWAG-147: morphs the live results dropdown (`#predictive-search-results`)
   * against `newRoot`, resolving both sides to that element explicitly
   * instead of morphing `predictiveSearchResults` (the ref wrapper, one DOM
   * level above the dropdown) directly against `newRoot`. `morph()` defaults
   * to `childrenOnly: true`, which only updates the CHILDREN of its first
   * argument to match the CHILDREN of its second - it requires both
   * arguments to be the same conceptual element. `predictiveSearchResults`
   * and the parsed `newRoot` are not: `newRoot` (or its `#predictive-search-results`
   * descendant) is one level further in than `predictiveSearchResults`'s
   * children. Morphing them directly compared the dropdown's OWN children
   * against the dropdown ITSELF, corrupting the dropdown root's attributes
   * every time (id/role="listbox"/class silently stripped, confirmed via a
   * DOM harness - see PR description) - the root cause of predictive search
   * "freezing" on stale content and losing its listbox semantics after the
   * first live query.
   * @param {string | Element} newRoot - Raw markup, or a parsed element,
   *   containing the new `#predictive-search-results` dropdown as itself or
   *   as a descendant (the two current callers differ here: the predictive
   *   search fetch returns the dropdown unwrapped, while a generic section
   *   fetch wraps it in a `.shopify-section` container).
   */
  #morphResults(newRoot) {
    const { predictiveSearchResults } = this.refs;
    const currentResults = predictiveSearchResults.querySelector('#predictive-search-results');

    let newResults;
    if (typeof newRoot === 'string') {
      const parsed = new DOMParser().parseFromString(newRoot, 'text/html');
      newResults = parsed.getElementById('predictive-search-results');
    } else if (newRoot instanceof Element) {
      newResults =
        newRoot.id === 'predictive-search-results'
          ? newRoot
          : newRoot.querySelector('#predictive-search-results');
    }

    if (currentResults && newResults) {
      morph(currentResults, newResults);
    } else {
      // Defensive fallback: preserve prior behavior rather than no-op if the
      // expected id is ever missing from either side. Warn because this path
      // reintroduces the SWAG-147 root-attribute-corruption failure mode
      // (see the function-level comment above) and would otherwise fail
      // silently.
      console.warn(
        '[predictive-search] #predictive-search-results missing from current or new markup; falling back to direct morph (may corrupt listbox root attributes - see SWAG-147).'
      );
      morph(predictiveSearchResults, newRoot);
    }

    // SWAG-148: the morph replaces the option nodes, so any option id the
    // input still references is now gone. A mid-flight Arrow (the fetch
    // debounce clears #currentIndex, but the user can re-navigate before the
    // request resolves) would otherwise leave aria-activedescendant pointing
    // at a destroyed node - violating the combobox contract that it must
    // reference an existing role="option". Fresh server markup carries no
    // aria-selected, so #currentIndex already reads -1 after the morph; we
    // only need to drop the stale attribute. Clearing it directly (rather
    // than via `#currentIndex = -1`) avoids the setter's searchInput.focus()
    // side effect firing on every morph, including idle/preload renders.
    this.refs.searchInput.removeAttribute('aria-activedescendant');

    this.#syncResultItemsTabIndex();
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ??
      -1
    );
  }

  set #currentIndex(index) {
    // SWAG-148: always clear first so a stale aria-activedescendant never
    // survives a bail-out below (e.g. transitioning to a zero-result state).
    this.refs.searchInput.removeAttribute('aria-activedescendant');

    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });
    this.refs.searchInput.focus();

    // SWAG-148: WAI-ARIA combobox pattern - DOM focus stays on the input;
    // aria-activedescendant communicates which listbox option is virtually
    // focused. Only set it when the active item carries a stable id (all
    // result-card snippets render one - see predictive-search.liquid,
    // predictive-search-resource-list.liquid, predictive-search-products-list.liquid).
    if (activeItem?.id) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector(
          '[data-single-result-url]'
        );
        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   * @param {boolean} [requireEmpty=false] - SWAG-147/148: re-check that the
   *   input is still empty at the moment this debounced call actually fires,
   *   not just when it was scheduled. #loadEmptyState's own empty-check runs
   *   before this 100ms debounce delay; without re-checking here, a user who
   *   types within that window has their typed value (and the fresh results
   *   that follow) wiped by this now-stale idle-preload reset landing after
   *   them - the #requestSeq guard on the morph itself doesn't cover this
   *   synchronous `searchInput.value = ''` side effect.
   */
  resetSearch = debounce((keepFocus = true, requireEmpty = false) => {
    if (requireEmpty && this.refs.searchInput.value.trim().length > 0) return;
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector('.predictive-search-results__inner')
        ?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    // SWAG-52: stamp this request's generation before the fetch starts.
    const seq = ++this.#requestSeq;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        // SWAG-52: bail if a newer search/reset request has started since
        // this one began, or if the input no longer holds the term this
        // fetch was for (user kept typing) - prevents a stale response from
        // painting over fresher results.
        if (seq !== this.#requestSeq) return;
        if (this.refs.searchInput.value.trim() !== searchTerm) return;

        // SWAG-147: see #morphResults for why this can't be
        // morph(predictiveSearchResults, resultsMarkup) directly.
        this.#morphResults(resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      'q',
      viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR ')
    );
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    // SWAG-52: stamp this request's generation before the async work starts.
    // Escape / the reset button / an emptied input are always the newest
    // request when invoked, so their own morph below still wins - this only
    // blocks a slower, superseded reset (e.g. the idle preload) from
    // clobbering a newer search or reset.
    const seq = ++this.#requestSeq;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(
        recentlyViewedMarkup,
        'text/html'
      );
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById(
        'predictive-search-products'
      );
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector(
        '#predictive-search-products'
      );
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    // SWAG-52: bail if a newer search/reset request has started since this
    // one began - prevents a slow idle-preload reset from painting over
    // fresher live search results.
    if (seq !== this.#requestSeq) return;

    // SWAG-147: see #morphResults for why this can't be
    // morph(predictiveSearchResults, parsedEmptySectionMarkup) directly.
    this.#morphResults(parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
